export type KeysOf<Type> = keyof Type;
export type ValuesOf<Type> = Type[KeysOf<Type>];

export type Opaque<Name, Type> = Type & { __TYPE__ : Name };

export type EmptyObject = Record<string, never>;

export type Omit<Type, K extends keyof Type> = Pick<Type, Exclude<keyof Type, K>>;
export type PartialBy<SomeType, OptionalKeys extends keyof SomeType> =
    Omit<SomeType, OptionalKeys> & Partial<Pick<SomeType, OptionalKeys>>;
export type GetPossibleUndefinedValueKeys<Type> =
    { [key in keyof Type] : undefined extends Type[key] ? key : never }[keyof Type];
export type MakeUndefinedValuesOptionalKeys<Type> = PartialBy<Type, GetPossibleUndefinedValueKeys<Type>>;

export type UnknownObject = Record<string, unknown>;

export type UnknownArray = ReadonlyArray<unknown>;

export type UnknownRecord = Record<string, unknown>;

export type ParametersExceptFirst<F> =
   // eslint-disable-next-line @typescript-eslint/no-explicit-any
   F extends (arg0 : any, ...rest : infer R) => any ? R : never;

export type ValueOrPromise<Type> = Promise<Type> | Type;

export type OptionalProperties<Type> = Exclude<{
    [Key in keyof Type] : Type extends Record<Key, Type[Key]> ? never : Key
}[keyof Type], undefined>;

export type UndefinedProperties<Type> = {
    [Key in keyof Type] -?: undefined extends Type[Key]
        ? Type extends Record<Key, Type[Key]>
            ? Key
            : never
        : never
}[keyof Type];

export type UndefinedPropertiesToOptional<Type> =
    & Partial<Pick<Type, UndefinedProperties<Type>>>
    & Pick<Type, Exclude<keyof Type, UndefinedProperties<Type>>>;

export type OptionalPropertiesToUndefined<Type> =
    & {
        [Key in OptionalProperties<Type>] -?: Type[Key]
    }
    & Pick<Type, Exclude<keyof Type, OptionalProperties<Type>>>;

export type RecursiveOptionalPropertiesToUndefined<Type> = Type extends { [key : string] : unknown }
    ? OptionalPropertiesToUndefined<{
        [Key in keyof Type] : RecursiveOptionalPropertiesToUndefined<Type[Key]>
    }>
    : Type;

export type RecursiveUndefinedPropertiesToOptional<Type> = Type extends { [key : string] : unknown }
    ? UndefinedPropertiesToOptional<{
        [Key in keyof Type] : RecursiveUndefinedPropertiesToOptional<Type[Key]>
    }>
    : Type;

export type RecursiveRequired<Type> = Type extends { [key : string] : unknown }
    ? { [Key in keyof Type] -?: RecursiveRequired<Type[Key]> }
    : Type extends ReadonlyArray<unknown>
        ? { [Key in keyof Type] -?: RecursiveRequired<Type[Key]> }
        : Type;

export type CombineObjectTypes<
    FirstObject,
    SecondObject
> = {
    [ Key in keyof FirstObject ] : Key extends keyof SecondObject
        ? SecondObject[Key]
        : FirstObject[Key]
} & SecondObject;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const assertTypeType = <BaseType, Type extends BaseType>() : void => undefined;

export type SetMember<SomeSet extends Set<unknown>> = SomeSet extends Set<infer Value>
    ? Value
    : never;

export type NonEmptyReadonlyArray<Type> = readonly [
    Type,
    ...ReadonlyArray<Type>
];

export type Never = Opaque<'Never', unknown>;
